/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import {
  FocusEvent, FormEvent, KeyboardEvent, useRef, useState, useEffect,
} from 'react';
import SearchIcon from 'assets/icons/search.svg';
import useModifiers from 'hooks/useModifiers';
import { BtnModifier } from 'components/btn';
import CloseIcon from 'assets/icons/close.svg';
import { TextfieldModifier } from './textfield';

export enum TypeaheadModifier {
  INTEGRATION = 'integration',
  OVERVIEW = 'overview',
  SMALL_DROPDOWN_SPACING = 'smallDropdownSpacing',
}

export type TypeaheadSuggestionItem = Record<string, unknown>;

export interface TypeaheadSuggestion {
  label?: string;
  items: TypeaheadSuggestionItem[];
}

export interface TypeaheadProps {
  filterByKey: string;
  suggestions: TypeaheadSuggestion[];
  showDropdown: boolean;
  placeholder?: string;
  hasButton?: boolean;
  hasFocus: boolean;
  modifiers?: TypeaheadModifier[];
  textfieldMods?: TextfieldModifier[];
  buttonMods?: BtnModifier;
  debounceMs?: number;
  disabled?: boolean;
  cb: (filteredSuggestions: TypeaheadSuggestion[]) => void;
  searchCb: (searchTerm: string) => void;
}

function Typeahead(props: TypeaheadProps) {
  const [currentOptions, setCurrentOptions] = useState(props.suggestions);
  const mods = useModifiers(props.modifiers);
  const textfieldMods = useModifiers(props.textfieldMods);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    debounceMs = 0, disabled = false,
  } = props;

  const getFilteredSuggestions = (search: string): TypeaheadSuggestion[] => {
    if (search === '') {
      return props.suggestions;
    }

    const result = props.suggestions.reduce((acc, curr) => {
      const filteredItems = (curr.items || []).filter((item) => (
        (item && item[props.filterByKey]) as string || ''
      ).toLowerCase().includes(search.toLowerCase()));

      return [...acc, { label: curr.label, items: filteredItems }];
    }, [] as TypeaheadSuggestion[]);
    return result;
  };

  const handleInputValueInsert = (inputValue: string) => {
    setValue(inputValue);
    const opt = getFilteredSuggestions(inputValue);
    setCurrentOptions(opt);

    if (inputValue.length === 0) {
      setIsMenuOpen(false);
      props.cb(props.suggestions);
    }

    props.cb(opt);
    //props.searchCb(inputValue);
  };

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    handleInputValueInsert(inputValue);
  };

  const handleOptionClick = (name: string, displayName: string) => {
    const filteredSuggestions = getFilteredSuggestions(name);
    setValue(displayName);
    setIsMenuOpen(false);
    setCurrentOptions(filteredSuggestions);
    props.cb(filteredSuggestions);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (e.relatedTarget) return;
    setIsMenuOpen(false);
  };

  const handleFocus = () => {
    if (props.suggestions.length > 0) {
      setIsMenuOpen(true);
    }
  };

  //debounce search callback
  useEffect(() => {
    if (value !== null) {
      const handler = () => {
        props.searchCb(value);
      };

      if (debounceMs && debounceMs > 0) {
        timerRef.current = setTimeout(() => handler(), debounceMs);
      } else {
        handler();
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
  };
}, [value]);

  return (
    <div className={`ct_typeahead ct_form ${disabled ? '_inactive' : '_active'} ${mods}`} tabIndex={0} onFocus={handleFocus} onBlur={handleBlur}>
      <div className="ct_form-control">
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={props.hasFocus}
          value={value}
          onChange={handleInputChange}
          className={`ct_textfield ${textfieldMods} ${props.hasButton ? '_hasButton' : ''}`}
          type="text"
          placeholder={props.placeholder}
          ref={inputRef}
        />
        {value.length > 0 && (
          <button type="button" onClick={() => { handleInputValueInsert(''); }} className="ct_btn _typehead _searchIcon">
            <CloseIcon />
          </button>
        )}
        {props.hasButton && (
        <button type="button" className={`ct_btn _typehead _searchIcon _noCursor ${props.buttonMods ? `_${props.buttonMods}` : ''}`}>
          <SearchIcon />
        </button>
        )}
      </div>

      {props.showDropdown && currentOptions.some((group: TypeaheadSuggestion) => group.items.length) && isMenuOpen ? (
        <div className={`ct_typeahead-options ${mods} ${isMenuOpen ? '_open' : ''}`}>
          {currentOptions.map((group: TypeaheadSuggestion, i: number) => (
            <div key={i} className={`ct_typeahead-group ${group.items && group.items.length ? '' : '_hidden'}`}>
              {group.label && (<div className="ct_typeahead-groupLabel">{group.label}</div>)}
              <div className="ct_typeahead-groupItems">
                {group.items.map((option: TypeaheadSuggestionItem, index: number) => (
                  <div
                    aria-hidden
                    key={index}
                    tabIndex={0}
                    className="ct_typeahead-option"
                    onClick={() => handleOptionClick((option && option[props.filterByKey]) as string || '', option.display_name as string)}
                  >
                    {option.display_name as string}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

Typeahead.defaultProps = { ...{} };

export default Typeahead;
